import { en, type MessageKey } from "./en";
import { de } from "./de";
import { fr } from "./fr";

/**
 * Translation, without React.
 *
 * Kept pure so it can be tested directly and reused from anywhere — including
 * the callable error mapper, which has no component to sit in.
 */

export type Locale = "en" | "de" | "fr";
export const LOCALES: readonly Locale[] = ["en", "de", "fr"] as const;
export const DEFAULT_LOCALE: Locale = "en";

const CATALOGUES: Record<Locale, Record<string, string>> = { en, de, fr };

/**
 * The BCP 47 tag each locale formats, sorts and pluralises with.
 *
 * French is plain `fr`, not `fr-FR` or `fr-CA`: the same Backstage serves Lyon
 * and Montréal, and the neutral tag gives both the conventions they share.
 */
export const BCP47: Record<Locale, string> = { en: "en-GB", de: "de-DE", fr: "fr" };

/** How each locale is named in a list of languages, in the reader's own language. */
export const LOCALE_LABEL_KEY: Record<Locale, MessageKey> = {
  en: "institution.locale.en",
  de: "institution.locale.de",
  fr: "institution.locale.fr",
};

/** The options of a language select, one per shipped locale. */
export const LOCALE_OPTIONS: { value: Locale; labelKey: MessageKey }[] = LOCALES.map((value) => ({
  value,
  labelKey: LOCALE_LABEL_KEY[value],
}));

/**
 * Which edition of the public website a locale links to, and the slug of its
 * sub-processor page there. The website is English and German only; a French
 * reader is sent to the English page rather than to a `/fr/` path that does
 * not exist.
 */
export const SITE_EDITION: Record<Locale, { prefix: "en" | "de"; subprocessors: string }> = {
  en: { prefix: "en", subprocessors: "subprocessors" },
  de: { prefix: "de", subprocessors: "auftragsverarbeiter" },
  fr: { prefix: "en", subprocessors: "subprocessors" },
};

/** A collator for sorting names the way a reader of this locale expects. */
export function collator(locale: Locale): Intl.Collator {
  return new Intl.Collator(BCP47[locale]);
}

export type { MessageKey };

export function isLocale(value: unknown): value is Locale {
  return typeof value === "string" && (LOCALES as readonly string[]).includes(value);
}

/**
 * Interpolates `{name}` placeholders.
 *
 * Only named placeholders, never positional ones: German and English put the
 * same values in different places in a sentence, and a positional scheme forces
 * a translator to keep an order that their language does not want.
 */
export function interpolate(template: string, params: Record<string, unknown>): string {
  return template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = params[name];
    // An unresolved placeholder is left visible rather than blanked. A sentence
    // with `{max}` in it is obviously broken; a sentence reading "That is more
    // than  rows" looks like something a person wrote.
    return value === undefined || value === null ? match : String(value);
  });
}

/** Whether the catalogues define this key at all. */
export function hasKey(key: string): boolean {
  return key in CATALOGUES[DEFAULT_LOCALE];
}

export function translate(
  locale: Locale,
  key: string,
  params: Record<string, unknown> = {}
): string {
  // `??` because a locale can arrive untyped — from storage, a URL, an old
  // account — and an unknown one should read as English, not throw.
  const catalogue = CATALOGUES[locale] ?? CATALOGUES[DEFAULT_LOCALE];
  const template = catalogue[key] ?? CATALOGUES[DEFAULT_LOCALE][key];

  // A missing key shows the key. It is ugly on purpose — the alternative is an
  // empty element that nobody notices until a teacher reports a blank button.
  // In development the context's `t()` throws instead; see I18nContext.
  if (template === undefined) return key;

  return interpolate(template, params);
}

/**
 * Plural selection.
 *
 * `Intl.PluralRules` rather than `count === 1`, because the two languages we
 * ship agree and the next one will not. Both English and German have `one` and
 * `other`; a language with `few` or `many` needs only a wider `forms` object,
 * not a different call site.
 */
export type PluralForms = Partial<Record<Intl.LDMLPluralRule, string>> & { other: string };

export function plural(locale: Locale, count: number, forms: PluralForms): string {
  const category = new Intl.PluralRules(BCP47[locale]).select(count);
  const template = forms[category] ?? forms.other;
  return interpolate(template, { count });
}

/** Every key the catalogues define. Used by the parity test. */
export function keys(): MessageKey[] {
  return Object.keys(en) as MessageKey[];
}

const STORAGE_KEY = "spontar.locale";

/**
 * The stored preference, then the browser's, then English.
 *
 * `localStorage` throws in a locked-down browser rather than returning null, so
 * every access is guarded. A back office that will not load because a privacy
 * setting is strict would be an absurd way to lose an institution.
 */
export function readStoredLocale(): Locale | undefined {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isLocale(stored) ? stored : undefined;
  } catch {
    return undefined;
  }
}

export function writeStoredLocale(locale: Locale): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Preference is not persisted. The session still works.
  }
}

export function detectLocale(): Locale {
  const stored = readStoredLocale();
  if (stored) return stored;

  const languages = typeof navigator === "undefined" ? [] : (navigator.languages ?? []);
  for (const language of languages) {
    const base = language.split("-")[0];
    if (isLocale(base)) return base;
  }
  return DEFAULT_LOCALE;
}
